// UUID import removed as we're using email as username
import {
  CognitoUserPool,
  CognitoUserAttribute,
  CognitoUser,
  AuthenticationDetails
} from 'amazon-cognito-identity-js';
import { setOrganizationId, setUserRole, removeOrganizationId, removeUserRole } from '../utils/organizationUtils';

const COGNITO_CONFIG = {
  UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
  ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID
};

// Create a user pool instance
const userPool = new CognitoUserPool(COGNITO_CONFIG);

// Storage for auth tokens
let authTokens = {
  accessToken: null,
  refreshToken: null,
  idToken: null,
  expiresAt: null
};

/**
 * Sign up a new user in Cognito
 * @param {string} email - User's email
 * @param {string} password - User's password
 * @param {Object} attributes - Additional user attributes
 * @returns {Promise<Object>} - Result of the sign-up operation
 */
export const signUp = async (email, password, attributes = {}) => {
  try {
    // Use email as the username
    const username = email;

    // Build attribute list
    const attributeList = [];

    // Add email as an attribute
    attributeList.push(new CognitoUserAttribute({
      Name: 'email',
      Value: email
    }));

    // Set email as the preferred login attribute
    attributeList.push(new CognitoUserAttribute({
      Name: 'preferred_username',
      Value: email
    }));

    // Add name if provided
    if (attributes.name) {
      attributeList.push(new CognitoUserAttribute({
        Name: 'name',
        Value: attributes.name
      }));
    }

    return new Promise((resolve, reject) => {
      userPool.signUp(username, password, attributeList, null, (err, result) => {
        if (err) {
          console.error('Error signing up:', err);
          reject(err);
          return;
        }
        // Include the username (email) in the result
        result.username = username;
        // Store the username in localStorage for later use
        localStorage.setItem('tempUsername', username);
        resolve(result);
      });
    });
  } catch (error) {
    console.error('Error in signUp:', error);
    throw error;
  }
};

/**
 * Confirm a user's registration with verification code
 * @param {string} email - User's email
 * @param {string} code - Verification code
 * @returns {Promise<Object>} - Result of the confirmation
 */
export const confirmSignUp = async (email, code) => {
  try {
    // Get the stored username from localStorage
    const username = localStorage.getItem('tempUsername');

    // If we have a stored username, use it; otherwise fall back to email
    const userData = {
      Username: username || email,
      Pool: userPool
    };

    const cognitoUser = new CognitoUser(userData);

    return new Promise((resolve, reject) => {
      cognitoUser.confirmRegistration(code, true, (err, result) => {
        if (err) {
          console.error('Error confirming sign up:', err);
          reject(err);
          return;
        }
        resolve(result);
      });
    });
  } catch (error) {
    console.error('Error in confirmSignUp:', error);
    throw error;
  }
};

/**
 * Fetch user data from API to get organization and role
 * @param {string} userId - The Cognito user ID (sub)
 * @param {string} idToken - The ID token for authorization
 * @returns {Promise<Object|null>} User data or null if not found
 */
const fetchUserDataFromApi = async (userId, idToken) => {
  try {
    const API_URL = 'https://xoxl3mvo9e.execute-api.us-east-1.amazonaws.com/prod/users';
    const response = await fetch(`${API_URL}/${userId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': idToken
      }
    });

    if (!response.ok) {
      if (response.status === 404) {
        // User not found - they may not have a subscription yet
        console.log('User not found in database - no subscription');
        return null;
      }
      console.error(`Failed to fetch user data: ${response.status}`);
      return null;
    }

    const data = await response.json();
    console.log('User data fetched:', data);
    return data;
  } catch (error) {
    console.error('Error fetching user data from API:', error);
    return null;
  }
};

/**
 * Sign in a user
 * @param {string} email - User's email
 * @param {string} password - User's password
 * @returns {Promise<Object>} - User session including tokens or challenge information
 */
export const signIn = async (email, password) => {
  try {
    // Get the stored username from localStorage if available
    const storedUsername = localStorage.getItem('tempUsername');

    const authenticationData = {
      Username: storedUsername || email,
      Password: password
    };

    const authenticationDetails = new AuthenticationDetails(authenticationData);

    const userData = {
      Username: storedUsername || email,
      Pool: userPool
    };

    const cognitoUser = new CognitoUser(userData);

    return new Promise((resolve, reject) => {
      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: async (session) => {
          // Store tokens
          authTokens = {
            accessToken: session.getAccessToken().getJwtToken(),
            refreshToken: session.getRefreshToken().getToken(),
            idToken: session.getIdToken().getJwtToken(),
            expiresAt: new Date().getTime() + session.getAccessToken().getExpiration() * 1000
          };

          // Store tokens in localStorage for persistence
          localStorage.setItem('authTokens', JSON.stringify(authTokens));

          try {
            // Get user ID from Cognito
            const userId = session.getIdToken().payload.sub;

            // Fetch user organization and role data from API
            const userDataResponse = await fetchUserDataFromApi(userId, authTokens.idToken);

            if (userDataResponse && userDataResponse.orgId) {
              // Store organization ID and role
              setOrganizationId(userDataResponse.orgId);
              setUserRole(userDataResponse.role || 'Staff');

              resolve({
                user: cognitoUser,
                session: session,
                tokens: authTokens,
                organizationId: userDataResponse.orgId,
                role: userDataResponse.role
              });
            } else {
              // User doesn't have an organization yet (no subscription)
              resolve({
                user: cognitoUser,
                session: session,
                tokens: authTokens,
                organizationId: null,
                role: null
              });
            }
          } catch (error) {
            console.error('Error fetching organization data:', error);
            // Continue with login even if organization fetch fails
            resolve({
              user: cognitoUser,
              session: session,
              tokens: authTokens
            });
          }
        },
        onFailure: (err) => {
          console.error('Error signing in:', err);
          reject(err);
        },
        newPasswordRequired: (userAttributes, requiredAttributes) => {
          // Delete sensitive information before returning
          delete userAttributes.email_verified;
          delete userAttributes.phone_number_verified;
          // Delete email to prevent "Cannot modify an already provided email" error
          delete userAttributes.email;

          console.log('New password required');

          // Return the challenge information
          resolve({
            challengeName: 'NEW_PASSWORD_REQUIRED',
            user: cognitoUser,
            userAttributes,
            requiredAttributes
          });
        }
      });
    });
  } catch (error) {
    console.error('Error in signIn:', error);
    throw error;
  }
};

/**
 * Sign out the current user
 */
export const signOut = () => {
  try {
    const currentUser = userPool.getCurrentUser();
    if (currentUser) {
      currentUser.signOut();
      // Clear stored tokens
      authTokens = {
        accessToken: null,
        refreshToken: null,
        idToken: null,
        expiresAt: null
      };
      localStorage.removeItem('authTokens');
      // Also clear organization ID, role, subscription choice, and temporary username
      removeOrganizationId();
      removeUserRole();
      localStorage.removeItem('subscriptionChoice');
      localStorage.removeItem('tempUsername');
    }
  } catch (error) {
    console.error('Error signing out:', error);
    throw error;
  }
};

/**
 * Get the current authenticated user
 * @returns {Promise<Object|null>} - Current user or null if not authenticated
 */
export const getCurrentUser = async () => {
  try {
    const currentUser = userPool.getCurrentUser();

    if (!currentUser) {
      return null;
    }

    return new Promise((resolve, reject) => {
      currentUser.getSession((err, session) => {
        if (err) {
          console.error('Error getting session:', err);
          reject(err);
          return;
        }

        if (!session.isValid()) {
          // Clear organizationId when session is invalid
          localStorage.removeItem('organizationId');
          resolve(null);
          return;
        }

        // Update tokens
        authTokens = {
          accessToken: session.getAccessToken().getJwtToken(),
          refreshToken: session.getRefreshToken().getToken(),
          idToken: session.getIdToken().getJwtToken(),
          expiresAt: new Date().getTime() + session.getAccessToken().getExpiration() * 1000
        };

        localStorage.setItem('authTokens', JSON.stringify(authTokens));

        // Get user attributes
        currentUser.getUserAttributes((err, attributes) => {
          if (err) {
            console.error('Error getting user attributes:', err);
            resolve({ user: currentUser, session: session });
            return;
          }

          const userAttributes = {};
          attributes.forEach(attr => {
            userAttributes[attr.getName()] = attr.getValue();
          });

          resolve({
            user: currentUser,
            session: session,
            attributes: userAttributes,
            tokens: authTokens
          });
        });
      });
    });
  } catch (error) {
    console.error('Error in getCurrentUser:', error);
    throw error;
  }
};

/**
 * Refresh the session tokens
 * @returns {Promise<Object>} - New session
 */
export const refreshSession = async () => {
  try {
    const currentUser = userPool.getCurrentUser();

    if (!currentUser) {
      throw new Error('No current user');
    }

    const refreshToken = authTokens.refreshToken;

    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    return new Promise((resolve, reject) => {
      currentUser.refreshSession(refreshToken, (err, session) => {
        if (err) {
          console.error('Error refreshing session:', err);
          reject(err);
          return;
        }

        // Update tokens
        authTokens = {
          accessToken: session.getAccessToken().getJwtToken(),
          refreshToken: session.getRefreshToken().getToken(),
          idToken: session.getIdToken().getJwtToken(),
          expiresAt: new Date().getTime() + session.getAccessToken().getExpiration() * 1000
        };

        localStorage.setItem('authTokens', JSON.stringify(authTokens));

        resolve({
          session: session,
          tokens: authTokens
        });
      });
    });
  } catch (error) {
    console.error('Error in refreshSession:', error);
    throw error;
  }
};

/**
 * Get the current authentication tokens
 * @returns {Object} - Current auth tokens
 */
export const getAuthTokens = () => {
  // Try to load from localStorage if not in memory
  if (!authTokens.accessToken && localStorage.getItem('authTokens')) {
    try {
      authTokens = JSON.parse(localStorage.getItem('authTokens'));
    } catch (e) {
      console.error('Error parsing auth tokens from localStorage:', e);
    }
  }

  // Check if tokens have expired
  if (authTokens.expiresAt && authTokens.expiresAt < new Date().getTime()) {
    console.log('Auth tokens have expired, clearing tokens and organization ID');
    // Clear tokens
    authTokens = {
      accessToken: null,
      refreshToken: null,
      idToken: null,
      expiresAt: null
    };
    localStorage.removeItem('authTokens');
    // Also clear organization ID
    localStorage.removeItem('organizationId');
  }

  return authTokens;
};

/**
 * Complete the new password challenge
 * @param {CognitoUser} user - The Cognito user object
 * @param {string} newPassword - The new password
 * @param {Object} userAttributes - User attributes required by the challenge
 * @returns {Promise<Object>} - User session including tokens
 */
export const completeNewPasswordChallenge = async (user, newPassword, userAttributes = {}) => {
  try {
    // Make a copy of userAttributes to avoid modifying the original object
    const cleanedAttributes = { ...userAttributes };

    // Remove email attribute to prevent "Cannot modify an already provided email" error
    delete cleanedAttributes.email;

    return new Promise((resolve, reject) => {
      user.completeNewPasswordChallenge(newPassword, cleanedAttributes, {
        onSuccess: (session) => {
          // Store tokens
          authTokens = {
            accessToken: session.getAccessToken().getJwtToken(),
            refreshToken: session.getRefreshToken().getToken(),
            idToken: session.getIdToken().getJwtToken(),
            expiresAt: new Date().getTime() + session.getAccessToken().getExpiration() * 1000
          };

          // Store tokens in localStorage for persistence
          localStorage.setItem('authTokens', JSON.stringify(authTokens));

          resolve({
            user: user,
            session: session,
            tokens: authTokens
          });
        },
        onFailure: (err) => {
          console.error('Error completing new password challenge:', err);
          reject(err);
        }
      });
    });
  } catch (error) {
    console.error('Error in completeNewPasswordChallenge:', error);
    throw error;
  }
};

export default {
  signUp,
  confirmSignUp,
  signIn,
  signOut,
  getCurrentUser,
  refreshSession,
  getAuthTokens,
  completeNewPasswordChallenge
};
